import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Divider,
  Chip,
  Paper,
  IconButton,
} from '@mui/material';
import { Close, Edit, Delete, Payment, LocalOffer } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { Appointment, AppointmentStatus, Client, Timeoff } from '../types/entities';

interface AppointmentInfoDialogProps {
  open: boolean;
  onClose: () => void;
  appointment: Appointment | null;
  clients?: Client[]; // Add clients prop to get fresh client data
  timeoffs?: Timeoff[]; // Add timeoffs prop for conflict detection
  showActions?: boolean;
  onEdit?: (appointment: Appointment) => void;
  onDelete?: (appointment: Appointment) => void;
}

const AppointmentInfoDialog: React.FC<AppointmentInfoDialogProps> = ({
  open,
  onClose,
  appointment,
  clients = [],
  timeoffs = [],
  showActions = false,
  onEdit,
  onDelete,
}) => {
  const { t } = useTranslation();
  if (!appointment) return null;

  // Get fresh client data if available, otherwise fall back to appointment.client
  const freshClient = clients.find(client => client.id === appointment.client?.id);
  const displayClient = freshClient || appointment.client;

  const formatDate = (date: Date): string => {
    // Handle UTC date from database and convert to local timezone
    const localDate = new Date(date);
    
    // Format as dd.mm.yyyy, hh:mm
    const day = String(localDate.getDate()).padStart(2, '0');
    const month = String(localDate.getMonth() + 1).padStart(2, '0');
    const year = localDate.getFullYear();
    const hours = String(localDate.getHours()).padStart(2, '0');
    const minutes = String(localDate.getMinutes()).padStart(2, '0');
    
    return `${day}.${month}.${year}, ${hours}:${minutes}`;
  };

  const formatPrice = (price: string): string => {
    const numPrice = Math.round(parseFloat(price));
    return `${numPrice} zł`;
  };

  const formatDuration = (minutes: number): string => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    
    if (hours === 0) {
      return `${mins} ${mins !== 1 ? t('appointments.minutes') : t('appointments.minute')}`;
    } else if (mins === 0) {
      return `${hours} ${hours !== 1 ? t('appointments.hours') : t('appointments.hour')}`;
    } else {
      return `${hours} ${hours !== 1 ? t('appointments.hours') : t('appointments.hour')} ${mins} ${mins !== 1 ? t('appointments.minutes') : t('appointments.minute')}`;
    }
  };

  const getStatusColor = (status: AppointmentStatus): 'default' | 'primary' | 'success' | 'error' | 'warning' => {
    switch (status) {
      case AppointmentStatus.NEW:
        return 'primary';
      case AppointmentStatus.CONFIRMED:
        return 'default'; // Will use custom purple styling
      case AppointmentStatus.COMPLETED:
        return 'success';
      case AppointmentStatus.CANCELED:
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusChipSx = (status: AppointmentStatus) => {
    if (status === AppointmentStatus.CONFIRMED) {
      return {
        backgroundColor: '#9c27b0',
        color: 'white',
        '&:hover': {
          backgroundColor: '#8e24aa',
        }
      };
    }
    return {};
  };

  // Check for timeoff conflict
  const hasTimeoffConflict = () => {
    if (!appointment.hairdresser?.id || timeoffs.length === 0 || !appointment.services || appointment.services.length === 0) return false;
    
    const appointmentStart = new Date(appointment.date);
    const totalDurationMinutes = calculateTotalDuration(appointment.services);
    const appointmentEnd = new Date(appointmentStart.getTime() + totalDurationMinutes * 60000);
    
    return timeoffs.some(timeoff => {
      if (timeoff.hairdresser?.id !== appointment.hairdresser?.id) return false;
      
      const timeoffStart = new Date(timeoff.startDate);
      const timeoffEnd = new Date(timeoff.endDate);
      
      // Check if appointment overlaps with timeoff
      return appointmentStart < timeoffEnd && appointmentEnd > timeoffStart;
    });
  };

  const getStatusLabel = (status: AppointmentStatus): string => {
    switch (status) {
      case AppointmentStatus.NEW:
        return t('appointments.new');
      case AppointmentStatus.CONFIRMED:
        return t('appointments.confirmed');
      case AppointmentStatus.COMPLETED:
        return t('appointments.completed');
      case AppointmentStatus.CANCELED:
        return t('appointments.canceled');
      default:
        return status;
    }
  };

  const calculateTotalPrice = (services: any[]): number => {
    return services.reduce((total, service) => total + parseFloat(service.price), 0);
  };

  const calculateTotalDuration = (services: any[]): number => {
    return services.reduce((total, service) => total + service.duration, 0);
  };

  const calculateServiceDiscountAmount = (service: any): number => {
    if (!service.discount) return 0;
    
    const servicePrice = parseFloat(service.price);
    if (service.discount.type === 'percent') {
      return (servicePrice * parseFloat(service.discount.amount)) / 100;
    } else {
      return Math.min(parseFloat(service.discount.amount), servicePrice);
    }
  };

  const calculateAppointmentDiscountAmount = (): number => {
    if (!appointment.discount) return 0;
    
    const totalServicePrice = calculateTotalPrice(appointment.services || []);
    if (appointment.discount.type === 'percent') {
      return (totalServicePrice * parseFloat(appointment.discount.amount)) / 100;
    } else {
      return Math.min(parseFloat(appointment.discount.amount), totalServicePrice);
    }
  };

  const calculateTotalDiscountAmount = (): number => {
    const serviceDiscounts = (appointment.services || []).reduce((total, service) => 
      total + calculateServiceDiscountAmount(service), 0);
    const appointmentDiscount = calculateAppointmentDiscountAmount();
    return serviceDiscounts + appointmentDiscount;
  };

  const calculateFinalPrice = (): number => {
    const totalServicePrice = calculateTotalPrice(appointment.services || []);
    const totalDiscountAmount = calculateTotalDiscountAmount();
    return totalServicePrice - totalDiscountAmount;
  };

  const calculateFinishTime = (): string => {
    if (!appointment.date || !appointment.services || appointment.services.length === 0) {
      return '';
    }
    
    const startDate = new Date(appointment.date);
    const totalDurationMinutes = calculateTotalDuration(appointment.services);
    const finishDate = new Date(startDate.getTime() + totalDurationMinutes * 60000);
    
    const day = String(finishDate.getDate()).padStart(2, '0');
    const month = String(finishDate.getMonth() + 1).padStart(2, '0');
    const year = finishDate.getFullYear();
    const hours = String(finishDate.getHours()).padStart(2, '0');
    const minutes = String(finishDate.getMinutes()).padStart(2, '0');
    
    return `${day}.${month}.${year}, ${hours}:${minutes}`;
  };

  const formatCreatedAt = (date: Date): string => {
    const localDate = new Date(date);
    const day = String(localDate.getDate()).padStart(2, '0');
    const month = String(localDate.getMonth() + 1).padStart(2, '0');
    const year = localDate.getFullYear();
    const hours = String(localDate.getHours()).padStart(2, '0');
    const minutes = String(localDate.getMinutes()).padStart(2, '0');
    
    return `${day}.${month}.${year}, ${hours}:${minutes}`;
  };

  const formatUpdatedAt = (date: Date): string => {
    const localDate = new Date(date);
    const day = String(localDate.getDate()).padStart(2, '0');
    const month = String(localDate.getMonth() + 1).padStart(2, '0');
    const year = localDate.getFullYear();
    const hours = String(localDate.getHours()).padStart(2, '0');
    const minutes = String(localDate.getMinutes()).padStart(2, '0');
    
    return `${day}.${month}.${year}, ${hours}:${minutes}`;
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            {t('appointments.appointmentDetails')}
          </Typography>
          <Box display="flex" alignItems="center" gap={1}>
            {showActions && (onEdit || onDelete) && (
              <>
                {onEdit && (
                  <IconButton
                    onClick={() => onEdit(appointment)}
                    color="primary"
                    size="small"
                    title={t('common.edit')}
                  >
                    <Edit />
                  </IconButton>
                )}
                {onDelete && (
                  <IconButton
                    onClick={() => onDelete(appointment)}
                    color="error"
                    size="small"
                    title={t('common.delete')}
                  >
                    <Delete />
                  </IconButton>
                )}
              </>
            )}
            <IconButton
              aria-label="close"
              onClick={onClose}
              sx={{ color: (theme) => theme.palette.grey[500] }}
            >
              <Close />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ px: 3, py: 2 }}>
        <Box display="flex" flexDirection="column" gap={4} sx={{ pt: 2 }}>
          {/* Basic Information */}
          <Paper elevation={1} sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom color="primary" sx={{ mb: 3 }}>
              {t('appointments.basicInformation')}
            </Typography>
            <Box display="flex" flexDirection="column" gap={3}>
              <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} gap={3}>
                <Box flex={1}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                    {t('appointments.startTime')}
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {formatDate(appointment.date)}
                  </Typography>
                </Box>
                <Box flex={1}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                    {t('appointments.finishTime')}
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {calculateFinishTime() || t('appointments.notAvailable')}
                  </Typography>
                </Box>
              </Box>
              <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} gap={3}>
                <Box flex={1}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                    {t('common.client')}
                  </Typography>
                  <Box>
                    <Typography variant="body1" fontWeight="medium" component="span">
                      {displayClient?.name || t('calendar.unknownClient')}
                    </Typography>
                    {displayClient?.phone_number && (
                      <Typography variant="body2" color="text.secondary" component="span" sx={{ ml: 1 }}>
                        • {displayClient.phone_number}
                      </Typography>
                    )}
                  </Box>
                </Box>
                <Box flex={1}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                    {t('common.hairdresser')}
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {appointment.hairdresser?.name || t('calendar.unknownHairdresser')}
                  </Typography>
                </Box>
              </Box>
              <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} gap={3}>
                <Box flex={1}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                    {t('common.status')}
                  </Typography>
                  <Chip
                    label={getStatusLabel(appointment.status)}
                    color={getStatusColor(appointment.status)}
                    variant="filled"
                    size="small"
                    sx={getStatusChipSx(appointment.status)}
                  />
                </Box>
                <Box flex={1}></Box>
              </Box>
            </Box>
          </Paper>

          {/* Timeoff Conflict Warning */}
          {hasTimeoffConflict() && (
            <Box sx={{ 
              p: 2, 
              backgroundColor: '#fff3cd', 
              border: '1px solid #ffeaa7', 
              borderRadius: 1,
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}>
              <Typography variant="body2" sx={{ color: '#856404', fontWeight: 600 }}>
                ⚠️ {t('appointments.timeoffConflictWarning')}
              </Typography>
            </Box>
          )}

          {/* Notes */}
          {appointment.notes && (
            <Paper elevation={1} sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom color="primary" sx={{ mb: 2 }}>
                {t('appointments.appointmentNotes')}
              </Typography>
              <Typography variant="body1">
                {appointment.notes}
              </Typography>
            </Paper>
          )}

          {/* Client Notes */}
          {displayClient?.notes && (
            <Paper elevation={1} sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom color="primary" sx={{ mb: 2 }}>
                {t('appointments.clientNotes')}
              </Typography>
              <Typography variant="body1">
                {displayClient.notes}
              </Typography>
            </Paper>
          )}

          {/* Services */}
          <Paper elevation={1} sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom color="primary" sx={{ mb: 3 }}>
              {t('common.services')} ({appointment.services?.length || 0})
            </Typography>
            {appointment.services && appointment.services.length > 0 ? (
              <Box display="flex" flexDirection="column" gap={2}>
                {appointment.services.map((service, index) => (
                  <Box key={index} sx={{ p: 3, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 2 }}>
                      <Typography variant="subtitle1" fontWeight="medium">
                        {service.name}
                      </Typography>
                      <Typography variant="subtitle1" fontWeight="medium" color="primary">
                        {formatPrice(service.price)}
                      </Typography>
                    </Box>
                    {service.description && (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                        {service.description}
                      </Typography>
                    )}
                    <Typography variant="body2" color="text.secondary">
                      {t('common.duration')}: {formatDuration(service.duration)}
                    </Typography>
                  </Box>
                ))}
                <Divider sx={{ my: 2 }} />
                <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ pt: 2 }}>
                  <Typography variant="h6">
                    {t('common.total')}
                  </Typography>
                  <Box textAlign="right">
                    <Typography variant="h6" color="primary" sx={{ mb: 0.5 }}>
                      {formatPrice(calculateTotalPrice(appointment.services).toString())}
                    </Typography>
                    {calculateTotalDiscountAmount() > 0 && (
                      <Typography variant="body2" color="success.main" sx={{ fontWeight: 'medium', mb: 0.5 }}>
                        {t('appointments.final')}: {calculateFinalPrice().toFixed(2)} zł
                      </Typography>
                    )}
                    <Typography variant="body2" color="text.secondary">
                      {formatDuration(calculateTotalDuration(appointment.services))}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                {t('appointments.noServicesAssigned')}
              </Typography>
            )}
          </Paper>

          {/* Discount Details */}
          {(calculateTotalDiscountAmount() > 0) && (
            <Paper elevation={1} sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom color="primary" sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                <LocalOffer sx={{ fontSize: 20 }} />
                {t('appointments.discountDetails')}
              </Typography>
              
              {/* Service-Level Discounts */}
              {appointment.services && appointment.services.some((s: any) => s.discount) && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" gutterBottom sx={{ color: 'text.secondary', mb: 2 }}>
                    {t('appointments.serviceLevelDiscounts')}
                  </Typography>
                  <Box display="flex" flexDirection="column" gap={1}>
                    {(appointment.services || []).map((service, index) => (
                      service.discount && (
                        <Box key={index} sx={{ 
                          p: 2, 
                          border: '1px solid', 
                          borderColor: 'success.light', 
                          borderRadius: 1,
                          backgroundColor: 'rgba(76, 175, 80, 0.1)'
                        }}>
                          <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Box>
                              <Typography variant="subtitle2" sx={{ fontWeight: 'medium' }}>
                                {service.name}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {service.discount.type === 'percent' 
                                  ? `${service.discount.amount}% ${t('appointments.discount')}`
                                  : `${service.discount.amount} zł ${t('appointments.discount')}`
                                }
                              </Typography>
                            </Box>
                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                              -{calculateServiceDiscountAmount(service).toFixed(2)} zł
                            </Typography>
                          </Box>
                        </Box>
                      )
                    ))}
                  </Box>
                </Box>
              )}

              {/* Appointment-Level Discount */}
              {appointment.discount && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle1" gutterBottom sx={{ color: 'text.secondary', mb: 2 }}>
                    {t('appointments.appointmentLevelDiscount')}
                  </Typography>
                  <Box sx={{ 
                    p: 2, 
                    border: '1px solid', 
                    borderColor: 'success.light', 
                    borderRadius: 1,
                    backgroundColor: 'rgba(76, 175, 80, 0.1)'
                  }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 'medium' }}>
                          {t('appointments.entireAppointment')}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {appointment.discount.type === 'percent' 
                            ? `${appointment.discount.amount}% ${t('appointments.discount')}`
                            : `${appointment.discount.amount} zł ${t('appointments.discount')}`
                          }
                        </Typography>
                      </Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                        -{calculateAppointmentDiscountAmount().toFixed(2)} zł
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              )}

              {/* Discount Summary */}
              <Divider sx={{ my: 2 }} />
              <Box sx={{ 
                p: 2, 
                backgroundColor: 'success.light', 
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'success.main'
              }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: 'success.dark' }}>
                    {t('appointments.totalDiscount')}:
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'success.dark' }}>
                    -{calculateTotalDiscountAmount().toFixed(2)} zł
                  </Typography>
                </Box>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: 'success.dark' }}>
                    {t('appointments.finalPrice')}:
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'success.dark' }}>
                    {calculateFinalPrice().toFixed(2)} zł
                  </Typography>
                </Box>
              </Box>
            </Paper>
          )}

          {/* Payment Details - Only show for completed appointments */}
          {appointment.status === AppointmentStatus.COMPLETED && appointment.payments && appointment.payments.length > 0 && (
            <Paper elevation={1} sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom color="primary" sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Payment sx={{ fontSize: 20 }} />
                {t('appointments.paymentDetails')}
              </Typography>
              <Box display="flex" flexDirection="column" gap={2}>
                {appointment.payments.map((payment, index) => (
                  <Box key={payment.id || index} sx={{ 
                    p: 2, 
                    border: '1px solid', 
                    borderColor: 'divider', 
                    borderRadius: 1,
                    backgroundColor: 'grey.50'
                  }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>
                          {payment.method === 'cash' ? t('appointments.cash') : 
                           payment.method === 'card' ? t('appointments.card') : 
                           payment.method === 'voucher' ? t('appointments.voucher') : 
                           payment.method}
                        </Typography>
                      </Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                        {parseFloat(payment.amount.toString()).toFixed(2)} zł
                      </Typography>
                    </Box>
                  </Box>
                ))}
                
                {/* Total Payment Summary */}
                <Box sx={{ 
                  mt: 2, 
                  p: 2, 
                  backgroundColor: 'primary.light', 
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'primary.main'
                }}>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'primary.contrastText' }}>
                      {t('appointments.totalPaid')}:
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'primary.contrastText' }}>
                      {appointment.payments.reduce((sum, payment) => sum + parseFloat(payment.amount.toString()), 0).toFixed(2)} zł
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Paper>
          )}

          {/* Metadata */}
          <Paper elevation={1} sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom color="primary" sx={{ mb: 3 }}>
              {t('appointments.metadata')}
            </Typography>
            <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} gap={3}>
              <Box flex={1}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                  {t('appointments.created')}
                </Typography>
                <Typography variant="body2">
                  {formatCreatedAt(appointment.createdAt)}
                </Typography>
              </Box>
              <Box flex={1}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                  {t('appointments.lastUpdated')}
                </Typography>
                <Typography variant="body2">
                  {formatUpdatedAt(appointment.updatedAt)}
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default AppointmentInfoDialog;
